import { useEffect, useMemo, useState } from "react";
import { api, EGG_ORDER, DEFECT_ORDER, fmt, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { User, Pencil, Trash2, Send, Building2, Plus, Clock } from "lucide-react";

export const COLOR_BTN = {
  kirmizi: { label: "Kırmızı", cls: "bg-red-600 text-white", ring: "ring-red-400" },
  beyaz: { label: "Beyaz", cls: "bg-stone-100 text-stone-900 border border-stone-300", ring: "ring-stone-400" },
  tintin: { label: "Tintin", cls: "bg-amber-700 text-white", ring: "ring-amber-400" },
  dairy: { label: "MİM Mandıra", cls: "bg-sky-600 text-white", ring: "ring-sky-400" },
  extra: { label: "Ekstra", cls: "bg-slate-700 text-white", ring: "ring-slate-400" },
};
const COLOR_ORDER = ["kirmizi", "beyaz", "tintin", "dairy", "extra"];
export const unitShort = (u) => (u === "kg" ? "kg" : u === "adet" ? "adet" : "krt");
export const money = (n) => `${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
const numIn = (v) => (v === "" || v == null ? 0 : Number(String(v).replace(",", ".")) || 0);

function ItemModal({ open, onClose, draft, onSave }) {
  const [c, setC] = useState("");
  const [p, setP] = useState("");
  useEffect(() => { if (open) { setC(draft?.cartons ? String(draft.cartons) : ""); setP(draft?.price ? String(draft.price) : ""); } }, [open, draft]);
  const total = numIn(c) * numIn(p);
  const unit = draft?.unit || "karton";
  const qtyLabel = unit === "kg" ? "Miktar (kg)" : unit === "adet" ? "Adet" : "Karton / Koli Adeti";
  const priceLabel = unit === "kg" ? "1 Kg Fiyatı (₺)" : unit === "adet" ? "1 Adet Fiyatı (₺)" : "1 Karton Fiyatı (₺)";
  const save = () => {
    if (!(numIn(c) > 0)) return toast.error("Miktar girin");
    onSave({ ...draft, cartons: unit === "kg" ? Math.round(numIn(c) * 1000) / 1000 : Math.round(numIn(c)), price: numIn(p) });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="sale-item-modal">
        <DialogHeader>
          <DialogTitle className="font-head text-base">{draft?.source_name} — {["dairy", "extra"].includes(draft?.breed) ? "" : `${COLOR_BTN[draft?.breed]?.label} `}{draft?.size_label}</DialogTitle>
        </DialogHeader>
        {draft?.available != null && <div className="text-xs text-muted-foreground -mt-2">Mevcut: {fmt(draft?.available)} {unitShort(unit)}</div>}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-muted-foreground">{qtyLabel}
            <input autoFocus inputMode={unit === "kg" ? "decimal" : "numeric"} type="text" value={c} onChange={(e) => setC(e.target.value.replace(/[^0-9.,]/g, ""))} data-testid="sale-item-cartons"
              className="mt-1 h-12 w-full rounded-lg border border-input bg-background px-3 text-lg font-semibold tabular-nums focus:ring-2 focus:ring-primary outline-none" />
          </label>
          <label className="text-xs font-semibold text-muted-foreground">{priceLabel}
            <input inputMode="decimal" type="text" value={p} onChange={(e) => setP(e.target.value.replace(/[^0-9.,]/g, ""))} data-testid="sale-item-price"
              className="mt-1 h-12 w-full rounded-lg border border-input bg-background px-3 text-lg font-semibold tabular-nums focus:ring-2 focus:ring-primary outline-none" />
          </label>
        </div>
        {draft?.available != null && numIn(c) > (draft?.available || 0) && <div className="text-xs text-amber-600" data-testid="sale-item-over">Uyarı: mevcut stoktan fazla.</div>}
        <div className="rounded-lg bg-secondary/60 px-3 py-2 flex justify-between text-sm"><span>Satır Tutarı</span><b className="tabular-nums" data-testid="sale-item-total">{money(total)}</b></div>
        <DialogFooter>
          <button onClick={save} data-testid="sale-item-add" className="w-full h-12 rounded-lg bg-primary text-primary-foreground font-semibold inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
            <Plus size={18} /> {draft?.id ? "Satırı Güncelle" : "Fişe Ekle"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SourceList({ stock, breed, size, lines, onPick, unit }) {
  const inDraft = (src) => lines.filter((l) => l.source === src && l.breed === breed && l.size === size).reduce((s, l) => s + l.cartons, 0);
  const rows = (stock.sources || []).map((s) => ({ ...s, qty: Number(s.breeds?.[breed]?.[size] || 0) })).filter((s) => s.qty > 0);
  if (!rows.length) return <div className="text-sm text-muted-foreground p-3 rounded-lg bg-secondary/40" data-testid="sale-no-source">Bu malın depoda stoğu yok.</div>;
  return (
    <div className="space-y-2" data-testid="sale-source-list">
      {rows.map((s) => {
        const pend = Number(stock.pending?.[`${s.source}|${breed}|${size}`] || 0);
        const d = inDraft(s.source);
        return (
          <button key={s.source} onClick={() => onPick(s)} data-testid={`sale-source-${s.source}`}
            className="w-full flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left hover:border-primary active:scale-[0.99] transition-[border-color,transform]">
            <Building2 size={18} className="text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{s.name}</div>
              <div className="text-xs text-muted-foreground">
                Mevcut: <b className="text-foreground tabular-nums">{fmt(s.qty)}</b> {unitShort(unit)}
                {pend > 0 && <span className="text-amber-600"> ({fmt(pend)} {unitShort(unit)} onay bekliyor)</span>}
                {d > 0 && <span className="text-primary"> · fişte {fmt(d)}</span>}
              </div>
            </div>
            <Plus size={18} className="text-muted-foreground" />
          </button>
        );
      })}
    </div>
  );
}

function Summary({ lines, onEdit, onDelete }) {
  const tc = lines.filter((l) => (l.unit || "karton") === "karton").reduce((s, l) => s + l.cartons, 0);
  const ta = lines.reduce((s, l) => s + l.cartons * l.price, 0);
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="sale-summary">
      <div className="px-4 py-3 border-b border-border font-head font-semibold text-sm">Satış Fişi Özeti</div>
      {!lines.length ? <div className="p-4 text-sm text-muted-foreground">Henüz kalem eklenmedi.</div> : (
        <div className="divide-y divide-border">
          {lines.map((l, i) => (
            <div key={l.id} className="px-4 py-2.5 flex items-center gap-2 text-sm" data-testid={`sale-line-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{["dairy", "extra"].includes(l.breed) ? "" : `${COLOR_BTN[l.breed]?.label} `}{l.size_label} <span className="text-muted-foreground font-normal">· {l.source_name}</span></div>
                <div className="text-xs text-muted-foreground tabular-nums">{fmt(l.cartons)} {unitShort(l.unit)} × {money(l.price)}</div>
              </div>
              <div className="font-semibold tabular-nums">{money(l.cartons * l.price)}</div>
              <button onClick={() => onEdit(l)} data-testid={`sale-line-edit-${i}`} className="p-2 rounded-md hover:bg-secondary"><Pencil size={15} /></button>
              <button onClick={() => onDelete(l.id)} data-testid={`sale-line-delete-${i}`} className="p-2 rounded-md hover:bg-secondary text-destructive"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="px-4 py-3 bg-secondary/50 flex justify-between text-sm">
        <span>Toplam Karton: <b className="tabular-nums" data-testid="sale-total-cartons">{fmt(tc)}</b></span>
        <span>Genel Toplam: <b className="tabular-nums text-primary" data-testid="sale-total-amount">{money(ta)}</b></span>
      </div>
    </div>
  );
}

// Pazarlamacı ve ön muhasebe ortak fiş düzenleyici
export function SaleEditor({ initial, submitLabel = "Muhasebeye Gönder", onSubmit }) {
  const { meta } = useApp();
  const dairyProducts = meta.dairy_products || [];
  const extraProducts = meta.extra_products || [];
  const extraId = meta.extra_source_id || "ekstra";
  const labelOf = (k) => [...(meta.egg_sizes || []), ...(meta.defects || []), ...dairyProducts, ...extraProducts].find((s) => s.key === k)?.label || k;
  const unitOf = (b, k) => (b === "extra" ? "adet" : b === "dairy" ? (dairyProducts.find((p) => p.key === k)?.unit || "adet") : "karton");
  const [stock, setStock] = useState({ sources: [], pending: {} });
  const [customer, setCustomer] = useState(initial?.customer_name || "");
  const [breed, setBreed] = useState("");
  const [size, setSize] = useState("");
  const [lines, setLines] = useState(() => (initial?.items || []).map((i) => ({ ...i, id: i.id || crypto.randomUUID() })));
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStock = () => api.get("/sales/stock", { params: initial?.id ? { exclude: initial.id } : {} }).then((r) => setStock(r.data)).catch(() => {});
  useEffect(() => { loadStock(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const sizeTotal = useMemo(() => {
    const t = {};
    (stock.sources || []).forEach((s) => Object.entries(s.breeds?.[breed] || {}).forEach(([k, v]) => { t[k] = (t[k] || 0) + Math.max(0, Number(v || 0)); }));
    return t;
  }, [stock, breed]);

  const pick = (s) => setDraft({ source: s.source, source_name: s.name, breed, size, size_label: labelOf(size), unit: unitOf(breed, size), available: s.qty });
  const saveLine = (l) => {
    setLines((ls) => (l.id ? ls.map((x) => (x.id === l.id ? { ...x, ...l } : x)) : [...ls, { ...l, id: crypto.randomUUID() }]));
    setDraft(null);
  };
  const submit = async () => {
    if (!customer.trim()) return toast.error("Müşteri adı girin");
    if (!lines.length) return toast.error("Fişe en az bir kalem ekleyin");
    setBusy(true);
    try {
      await onSubmit({ customer_name: customer.trim(), items: lines.map(({ source, breed: b, size: s, cartons, price }) => ({ source, breed: b, size: s, cartons, price })) });
      if (!initial) { setCustomer(""); setLines([]); setBreed(""); setSize(""); }
      loadStock();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-5 min-w-0" data-testid="sale-editor">
      <label className="block">
        <span className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">1 · Müşteri Adı / Cari</span>
        <div className="mt-1.5 relative">
          <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Müşteri adını yazın" data-testid="sale-customer"
            className="h-12 w-full rounded-xl border border-input bg-background pl-10 pr-3 text-base focus:ring-2 focus:ring-primary outline-none" />
        </div>
      </label>

      {customer.trim() && (
        <div>
          <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">2 · Yumurta Rengi / Mandıra / Ekstra</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {COLOR_ORDER.map((b) => (
              <button key={b} onClick={() => { setBreed(b); setSize(""); }} data-testid={`sale-color-${b}`}
                className={`h-16 rounded-2xl text-base sm:text-lg font-bold transition-transform active:scale-95 ${COLOR_BTN[b].cls} ${breed === b ? `ring-4 ${COLOR_BTN[b].ring} scale-[1.02]` : "opacity-80"}`}>
                {COLOR_BTN[b].label}
              </button>
            ))}
          </div>
        </div>
      )}

      {breed === "extra" && (
        <div>
          <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">3 · Ekstra Malzeme</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {extraProducts.map((p) => (
              <button key={p.key} onClick={() => setDraft({ source: extraId, source_name: "Ekstra", breed: "extra", size: p.key, size_label: p.label, unit: "adet", available: null })} data-testid={`sale-extra-${p.key}`}
                className="rounded-xl border border-border bg-card px-2 py-3 text-sm font-semibold hover:border-primary active:scale-[0.98] transition-[border-color,transform]">
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {breed && breed !== "extra" && (
        <div>
          <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">3 · {breed === "dairy" ? "Mandıra Ürünü" : "Boy / Cins"}</div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {(breed === "dairy" ? dairyProducts.map((p) => p.key) : [...EGG_ORDER, ...DEFECT_ORDER]).map((k) => {
              const q = sizeTotal[k] || 0;
              return (
                <button key={k} onClick={() => setSize(k)} disabled={q <= 0} data-testid={`sale-size-${k}`}
                  className={`rounded-xl border px-2 py-2.5 text-sm font-semibold transition-colors disabled:opacity-35 disabled:cursor-not-allowed ${size === k ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary"}`}>
                  <div className="truncate">{labelOf(k)}</div>
                  <div className={`text-[11px] font-normal tabular-nums ${size === k ? "opacity-90" : "text-muted-foreground"}`}>{fmt(q)} {unitShort(unitOf(breed, k))}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {breed && size && (
        <div>
          <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">4 · Stoktaki Firma / Kaynak</div>
          <SourceList stock={stock} breed={breed} size={size} lines={lines} onPick={pick} unit={unitOf(breed, size)} />
        </div>
      )}

      <Summary lines={lines} onEdit={(l) => setDraft({ ...l, available: l.source === extraId ? null : Number((stock.sources || []).find((s) => s.source === l.source)?.breeds?.[l.breed]?.[l.size] || 0) })}
        onDelete={(id) => setLines((ls) => ls.filter((x) => x.id !== id))} />

      <button onClick={submit} disabled={busy} data-testid="sale-submit"
        className="w-full h-14 rounded-xl bg-primary text-primary-foreground text-base font-bold inline-flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60 transition-opacity">
        {initial ? <Clock size={18} /> : <Send size={18} />} {submitLabel}
      </button>

      <ItemModal open={!!draft} draft={draft} onClose={() => setDraft(null)} onSave={saveLine} />
    </div>
  );
}
